/**
 * Recent Mentions - localStorage persistence for quick access
 *
 * Stores recently selected mentions to provide quick re-selection
 * in the autocomplete dropdown. Supports namespacing by organization ID.
 */
import type { MentionSegment, MentionType } from './types';

const RECENT_MENTIONS_KEY_PREFIX = 'terramatrix_recent_mentions';
const MAX_RECENT_MENTIONS = 5;

// Current organization context for namespacing
let currentOrganizationId: string | null = null;

/**
 * Set the current organization context for localStorage namespacing
 * Call this when organization context changes (login, org switch)
 */
export function setRecentMentionsOrganization(orgId: string | null): void {
  currentOrganizationId = orgId;
}

/**
 * Get the namespaced localStorage key
 */
function getStorageKey(): string {
  return currentOrganizationId
    ? `${RECENT_MENTIONS_KEY_PREFIX}_${currentOrganizationId}`
    : RECENT_MENTIONS_KEY_PREFIX;
}

/**
 * Get recent mentions from localStorage, optionally filtered by type
 */
export function getRecentMentions(type?: MentionType): MentionSegment[] {
  try {
    const stored = localStorage.getItem(getStorageKey());
    const raw: unknown = stored ? JSON.parse(stored) : [];
    const all: MentionSegment[] = Array.isArray(raw) ? raw as MentionSegment[] : [];
    return type ? all.filter((m) => m.type === type) : all;
  } catch {
    return [];
  }
}

/**
 * Add a mention to recent history
 * Removes duplicates and maintains max size
 */
export function addRecentMention(mention: MentionSegment): void {
  try {
    const recent = getRecentMentions();
    // Remove if already exists (will be re-added at front)
    const filtered = recent.filter((m) => m.id !== mention.id);
    const updated = [mention, ...filtered].slice(0, MAX_RECENT_MENTIONS);
    localStorage.setItem(getStorageKey(), JSON.stringify(updated));
  } catch {
    // Silently fail if localStorage is unavailable (e.g., private browsing)
  }
}

/**
 * Clear all recent mentions from localStorage for current organization
 */
export function clearRecentMentions(): void {
  try {
    localStorage.removeItem(getStorageKey());
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Clear all recent mentions for all organizations (used on logout)
 */
export function clearAllRecentMentions(): void {
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(RECENT_MENTIONS_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
    currentOrganizationId = null;
  } catch {
    // Silently fail if localStorage is unavailable
  }
}
